import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaUser, FaChevronDown, FaSignOutAlt, FaTachometerAlt, FaCog, FaUserCircle, FaBars, FaTimes, FaLock, FaUserFriends, FaShieldAlt } from 'react-icons/fa';
import AdminPinModal from '../AdminPinModal/AdminPinModal';
import ConsentRequestModal from '../ConsentRequestModal/ConsentRequestModal';
import FamilyPrivacyModal from '../FamilyPrivacyModal/FamilyPrivacyModal';
import FamilyAssociationModal from '../FamilyAssociationModal/FamilyAssociationModal';
import './Header.css';
import API_BASE_URL from '../../config/api';

const isTokenExpired = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded).split('').map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    );
    const payload = JSON.parse(json);
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingAdminSwitch, setPendingAdminSwitch] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [targetConsentUser, setTargetConsentUser] = useState(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showFamilyAssocModal, setShowFamilyAssocModal] = useState(false);
  const dropdownRef = useRef(null);

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('roles');
    localStorage.removeItem('linkedUsers');
    localStorage.removeItem('userId');
    localStorage.removeItem('primaryUserId');
    localStorage.removeItem('acceptedRegulation');
    setIsLoggedIn(false);
    setDropdownOpen(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const name = localStorage.getItem('userName');
    const type = localStorage.getItem('userType');
    const rolesStr = localStorage.getItem('roles');
    const linkedUsersStr = localStorage.getItem('linkedUsers');
    const storedUserId = localStorage.getItem('userId');

    if (token && name && !isTokenExpired(token)) {
      setIsLoggedIn(true);
      setUserName(name);
      setUserId(storedUserId ? parseInt(storedUserId) : null);

      if (rolesStr) {
        try {
          setUserRoles(JSON.parse(rolesStr));
        } catch (e) {
          setUserRoles(type ? [type] : ['User']);
        }
      } else {
        setUserRoles(type ? [type] : ['User']);
      }

      if (linkedUsersStr) {
        try {
          setLinkedUsers(JSON.parse(linkedUsersStr));
        } catch (e) {
          setLinkedUsers([]);
        }
      }
    } else if (token && isTokenExpired(token)) {
      clearSession();
    }
  }, [location]);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && isTokenExpired(token)) {
        clearSession();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('roles');
    localStorage.removeItem('linkedUsers');
    localStorage.removeItem('userId');
    localStorage.removeItem('primaryUserId');
    localStorage.removeItem('acceptedRegulation');
    setIsLoggedIn(false);
    setDropdownOpen(false);
    navigate('/');
  };

  const handleSwitchUser = async (targetUserId, link) => {
    try {
      const token = localStorage.getItem('token');
      const primaryUserId = localStorage.getItem('primaryUserId');
      const response = await fetch(`${API_BASE_URL}/api/auth/switch-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Primary-User-Id': primaryUserId || ''
        },
        body: JSON.stringify({ targetUserId, primaryUserId: primaryUserId ? parseInt(primaryUserId) : null })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        localStorage.setItem('roles', JSON.stringify(data.roles));
        localStorage.setItem('userName', `${data.firstName} ${data.lastName}`);
        localStorage.setItem('userEmail', data.email);
        localStorage.setItem('userId', data.id.toString());
        localStorage.setItem('acceptedRegulation', data.acceptedRegulation ? 'true' : 'false');
        if (data.linkedUsers) {
          localStorage.setItem('linkedUsers', JSON.stringify(data.linkedUsers));
        }

        navigate(link);
        window.location.reload();
      } else {
        const err = await response.json();
        alert(err.message || 'Erro ao trocar de utilizador');
      }
    } catch (err) {
      console.error('Error switching user:', err);
    }
  };

  const handlePinSuccess = () => {
    setShowPinModal(false);
    if (pendingAdminSwitch) {
      if (pendingAdminSwitch.userId && pendingAdminSwitch.userId !== userId) {
        handleSwitchUser(pendingAdminSwitch.userId, pendingAdminSwitch.link);
      } else {
        navigate(pendingAdminSwitch.link);
      }
    }
  };

  const getMenuOptions = () => {
    const options = [];
    const currentId = parseInt(userId);

    const roles = userRoles.map(r => r.toLowerCase());

    const isAdmin = roles.includes('admin');
    const isCoordenador = roles.includes('coordenador') || roles.includes('coordinator');
    const isTreinador = roles.includes('treinador');
    const isAtleta = roles.includes('atleta');
    const hasSocioRole = roles.includes('socio');
    const isDefaultUser = roles.includes('user');

    if (isAdmin) {
      options.push({ icon: <FaTachometerAlt />, label: 'Dashboard Admin', link: '/dashboard-admin', userId: currentId });
    }

    if (isCoordenador) {
      options.push({ icon: <FaTachometerAlt />, label: 'Dashboard Coordenador', link: '/dashboard-coordenador', userId: currentId });
    }

    if (isTreinador) {
      options.push({ icon: <FaTachometerAlt />, label: 'Dashboard Treinador', link: '/dashboard-treinador', userId: currentId });
    }

    if (isAtleta) {
      options.push({ icon: <FaTachometerAlt />, label: 'Dashboard Atleta', link: '/dashboard-atleta', userId: currentId });
    }

    if ((hasSocioRole || isDefaultUser) && !isTreinador && !isAtleta && (!isAdmin || (!isCoordenador && !isTreinador && !isAtleta))) {
      options.push({ icon: <FaTachometerAlt />, label: 'Dashboard Sócio', link: '/dashboard-socio', userId: currentId });
    }

    if (options.length === 0) {
      options.push({ icon: <FaTachometerAlt />, label: 'Dashboard', link: '/', userId: currentId });
    }

    return options;
  };

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''}`}>
      <div className="container header-container">
        <div className="logo">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/CDP_logo.png" alt="Logo CDP" className="logo-img" />
            <div className="logo-text">
              <h1>Clube Desportivo</h1>
              <span>Da Póvoa</span>
            </div>
          </Link>
        </div>
        <div className="mobile-menu-icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </div>

        <nav className={`navbar ${mobileMenuOpen ? 'active' : ''}`}>
          <div className="nav-links">
            <Link to="/" className={location.pathname === '/' ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
              Início
            </Link>
            <Link to="/clube" className={location.pathname === '/clube' ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
              Clube
            </Link>
            <Link to="/modalidades" className={location.pathname === '/modalidades' ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
              Modalidades
            </Link>
            <Link to="/noticias" className={location.pathname === '/noticias' ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
              Notícias
            </Link>
            <Link to="/bilheteria" className={location.pathname === '/bilheteria' ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
              Bilheteira
            </Link>
            <Link to="/loja" className={location.pathname.startsWith('/loja') ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
              Loja
            </Link>
            <Link to="/contactos" className={location.pathname === '/contactos' ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
              Contactos
            </Link>
          </div>

          {isLoggedIn ? (
            <div className="user-menu" ref={dropdownRef}>
              <button
                className="user-menu-button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <FaUser className="user-icon" />
                <FaChevronDown className={`chevron ${dropdownOpen ? 'open' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <img src="/CDP_logo.png" alt="Logo CDP" className="logo-img" />
                    <div className="logo-text">
                      <h1>Clube Desportivo</h1>
                      <span>Da Póvoa</span>
                    </div>
                  </div>
                  {getMenuOptions().map((option, index) => {
                    if (option.isDivider) {
                      return <div key={index} className="dropdown-divider"></div>;
                    }

                    return (
                      <Link
                        key={index}
                        to={option.link}
                        className={`dropdown-item ${option.isSwitch ? 'profile-switch' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (option.isPrivacyModal) {
                            setShowPrivacyModal(true);
                            setDropdownOpen(false);
                            setMobileMenuOpen(false);
                          } else if (option.isFamilyAssocModal) {
                            setShowFamilyAssocModal(true);
                            setDropdownOpen(false);
                            setMobileMenuOpen(false);
                          } else if (option.requiresConsent) {
                            setTargetConsentUser(option.linkedUser);
                            setShowConsentModal(true);
                            setDropdownOpen(false);
                            setMobileMenuOpen(false);
                          } else if (option.label.includes('Dashboard Admin') || option.requiresPin) {
                            setPendingAdminSwitch({ userId: option.userId, link: option.link });
                            setShowPinModal(true);
                            setDropdownOpen(false);
                            setMobileMenuOpen(false);
                          } else if (option.userId && option.userId !== userId) {
                            handleSwitchUser(option.userId, option.link);
                          } else {
                            navigate(option.link);
                            setDropdownOpen(false);
                            setMobileMenuOpen(false);
                          }
                        }}
                      >
                        {option.icon}
                        <span>{option.label}</span>
                      </Link>
                    );
                  })}
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout" onClick={handleLogout}>
                    <FaSignOutAlt />
                    <span>Sair</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="btn-login" onClick={() => setMobileMenuOpen(false)}>
              <i className="fa-regular fa-user"></i> Login
            </Link>
          )}
        </nav>
      </div>

      <AdminPinModal
        isOpen={showPinModal}
        targetUserId={pendingAdminSwitch?.userId}
        onSuccess={handlePinSuccess}
        onClose={() => setShowPinModal(false)}
      />

      <ConsentRequestModal
        isOpen={showConsentModal}
        targetUser={targetConsentUser}
        onClose={() => setShowConsentModal(false)}
      />

      <FamilyPrivacyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

      <FamilyAssociationModal
        isOpen={showFamilyAssocModal}
        onClose={() => setShowFamilyAssocModal(false)}
      />
    </header>
  );
};

export default Header;
